import * as React from 'react'
import { ArrowDownToLine } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePersistStore } from '@/stores/persistStore'
import type { ParseResult } from '@/lib/fileParser'

type ModuleKey = 'sp80022' | 'sp80090b' | 'tunableBias' | 'gaussian'

interface ImportDataBannerProps {
  /** Current module key — excluded from the list */
  currentModule: ModuleKey
  /** Which data kinds this module accepts */
  acceptedKinds: Array<'binary' | 'symbol' | 'continuous'>
  /** Called when user imports data from another module */
  onImport: (data: ParseResult) => void
}

/**
 * Shows a compact banner with buttons to import data from other modules
 * that currently have data loaded. Only shows if other modules have compatible data.
 */
export function ImportDataBanner({ currentModule, acceptedKinds, onImport }: ImportDataBannerProps) {
  const { getAvailableInputs, getModuleInput } = usePersistStore()

  const sources = React.useMemo(() => {
    return getAvailableInputs().filter(
      (s) => s.module !== currentModule && acceptedKinds.includes(s.kind as any)
    )
  }, [getAvailableInputs, currentModule, acceptedKinds])

  if (sources.length === 0) return null

  return (
    <div className="overflow-hidden rounded-md bg-primary/5 border border-primary/20 px-3 py-2" role="region" aria-label="Import data from other modules">
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <ArrowDownToLine className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden="true" />
        <span className="text-xs text-muted-foreground shrink-0">Use data from:</span>
        {sources.map((s) => (
          <Button
            key={s.module}
            variant="outline"
            size="sm"
            className="h-6 text-[11px] px-2 gap-1 max-w-full overflow-hidden"
            onClick={() => {
              const data = getModuleInput(s.module)
              if (data) onImport(data)
            }}
          >
            <span className="shrink-0">{s.label}</span>
            <span className="text-muted-foreground font-normal truncate">
              ({s.fileName}, {s.sampleCount.toLocaleString()})
            </span>
          </Button>
        ))}
      </div>
    </div>
  )
}
